import edit from "@/assets/edit.svg";
import trashSolid from "@/assets/trash-solid.svg";
import Button, { BUTTON_VARIANT } from "@/common/elements/Button";
import Select from "@/common/elements/InputElements/Select";
import TextField from "@/common/elements/InputElements/TextField";
import Table from "@/common/elements/Table";
import {
  Flex,
  IntakeSectionHeader,
  StyledFlex,
  StyledGridCol,
  StyledGridRow,
} from "@/common/styles";
import { useEffect, useRef, useState } from "react";
import { Col, Popconfirm, Row } from "antd";
import {
  FormContainer,
  InmateDetailsTableContainer,
  TableHeader,
  TableHeaderButton,
} from "../InmateDetails/styles";
import CourtInformationModal, { MODE } from "./CourtInformationModal";
import FormItem from "@/common/elements/InputElements/FormItem.jsx";
import {
  getOptionsFromRefList,
  getRefListNameByID,
  isNonEmptyVariable,
} from "@/util/common_function";
import CommonDatePicker from "@/common/elements/DatePicker";
import dayjs from "dayjs";
import { useDispatch, useSelector } from "react-redux";
import { PlusOutlined } from "@ant-design/icons";
import { v4 as uuidV4 } from "uuid";
import { FieldSecurityProvider } from "@/contexts/FieldSecurityContext";
import SecureFormItem from "@/common/SecureFormItem";
import { SCHEMA_NAME } from "@/util/common_variable";
import useGridColumnConfig from "@/hooks/useGridColumnConfig";

const CourtInformationHeaders = [
  { key: "courtName", text: "Court Name" },
  { key: "courtDateTime", text: "Court Date & Time" },
  { key: "offenderDisposition", text: "Disposition" },
  { key: "courtReason", text: "Court Reason" },
  { key: "comments", text: "Comments" },
  { key: "actions", text: "Actions" },
];

const CourtInformation = ({ courtListsData, updateCourtListForm, startingPoint="" }) => {
  const dispatch = useDispatch();
  const bookings = useSelector((state) => state?.bookings?.bookings);
  const { bookingsFieldsEnabled } = useSelector(
    (state) => state?.bookings
  );

  // "Config" — drag-reorder/resize columns, persisted per-user (see
  // docs/plans/grid-column-config-feature.md).
  const {
    headers: courtHeaders,
    isConfiguring,
    configButtonLabel,
    startConfiguring,
    confirmSave,
    discardChanges,
    dataTableRef,
  } = useGridColumnConfig("Bookings:Court", CourtInformationHeaders);

  const courtInfoRef = useRef();
  const [modalShown, setModalShown] = useState(false);
  const [tempCourtListsData, setTempCourtListsData] = useState([]);
  const [selectedCourtsData, setSelectedCourtsData] = useState(null);
  const { userDetail } = useSelector((s) => s?.userDetail);
  const fullSearch = useSelector(state => state.fullSearch);
  const [selectedCourt, setSelectedCourt] = useState(null);

  useEffect(() => {
    if (courtListsData?.length) {
      setTempCourtListsData(() => courtListsData);
    } else {
      setTempCourtListsData(() => []);
    }
  }, [courtListsData]);

  const onModalHide = () => {
    setModalShown(false);
    cancelAction();
  };

  const onAddClick = () => {
    setSelectedCourtsData(() => null);
    setModalShown(true);
  };

  const onDeleteClick = () => {
    if (!selectedCourtsData) return;

    const data = tempCourtListsData?.filter(
      (p) => p?.uuid !== selectedCourtsData?.uuid
    );

    setTempCourtListsData(data);
    updateCourtListFormLocal(data);
    setSelectedCourtsData(null);
  };

  const cancelAction = () => {
    setSelectedCourtsData(() => null);
  };

  const onCourtInformationSaveClick = () => {
    courtInfoRef?.current?.form?.validateFields().then((values) => {
      values.courtDateTime = isNonEmptyVariable(values?.courtDateTime)
        ? dayjs(values?.courtDateTime)?.format("MM/DD/YYYY HH:mm")
        : null;
      values.expectedBackDateTime = isNonEmptyVariable(
        values?.expectedBackDateTime
      )
        ? dayjs(values?.expectedBackDateTime)?.format("MM/DD/YYYY HH:mm")
        : null;
      const courtDate = isNonEmptyVariable(values?.courtDateTime)
        ? values?.courtDateTime?.split(" ")[0]
        : null;
      const courtTime = isNonEmptyVariable(values?.courtDateTime)
        ? values?.courtDateTime?.split(" ")[1]
        : null;

      const expectedDate = isNonEmptyVariable(values?.expectedBackDateTime)
        ? values?.expectedBackDateTime?.split(" ")[0]
        : null;
      const expectedTime = isNonEmptyVariable(values?.expectedBackDateTime)
        ? values?.expectedBackDateTime?.split(" ")[1]
        : null;

      const idx = tempCourtListsData?.findIndex(
        (p) => p?.uuid === selectedCourtsData?.uuid
      );

      const courtOptions = getOptionsFromRefList("Court", false, false, true);
      const houseTrackData = {};
      houseTrackData.actualDate= null;
      houseTrackData.actualTime = null;
      houseTrackData.approvingOfficerStaffID = userDetail?.staffId;
      houseTrackData.comments = values.comments;
      houseTrackData.expectedBackDate = expectedDate;
      houseTrackData.expectedBackTime = expectedTime;
      houseTrackData.scheduledDate = courtDate;
      houseTrackData.scheduledTime = courtTime;
      houseTrackData.inmateLocation = "Loan";
      houseTrackData.loanType = "Court";
      houseTrackData.movementStatus = "Pending";
      houseTrackData.bookingId = bookings.bookingId;
      houseTrackData.moveReason = "Court";
      houseTrackData.loanDestination = "others - See comments";
      houseTrackData.court = courtOptions.find((item) => item.value === values.courtNameID)?.label;
      let data = tempCourtListsData;
      if (idx !== -1) {
        data = data?.map((p) => {
          if (p?.uuid === selectedCourtsData?.uuid) {
            const existingTrackingId = p?.inmateTracking?.[0]?.inmateTrackingID || null;
            return {
              ...p,
              ...values,
              courtDate: courtDate,
              courtTime: courtTime,
              expectedDate: expectedDate,
              expectedTime: expectedTime,
              inmateTracking: [
                {
                  ...houseTrackData,
                  inmateTrackingID: existingTrackingId, // Preserve ID like your example
                },
              ],
            };
          }
          return p;
        });
      } else {
        const newObj = {
          ...values,
          courtDate: courtDate,
          courtTime: courtTime,
          expectedDate: expectedDate,
          expectedTime: expectedTime,
          uuid: uuidV4(),
          inmateTracking: [
            {
              ...houseTrackData,
            },
          ],
        };

        data.push(newObj);
      }

      setTempCourtListsData(data);
      updateCourtListFormLocal(data);

      onModalHide();
    });
  };

  const updateCourtListFormLocal = (data) => {
    updateCourtListForm(data);
  };

  return (
    <>
      <FieldSecurityProvider
        entity={SCHEMA_NAME.COURT}
        startingPoint={startingPoint}
        bookingsFieldsEnabled={bookingsFieldsEnabled}
      >
      <IntakeSectionHeader>
        <span>Court Information</span>
      </IntakeSectionHeader>
      <FormContainer>
        <StyledGridRow gutter={16}>
          <StyledGridCol lg={6}>
            <SecureFormItem
              fieldName="offenderDispositionID"
              formItemProps={{
                name: "offenderDispositionID",
                label: "Current Disposition",
                required: !fullSearch,
                rules: !fullSearch
                  ? [
                      {
                        required: true,
                        message: "Current Disposition is required",
                      },
                    ]
                  : [],
              }}
            >
              <Select
                options={getOptionsFromRefList(
                  "OffenderDisposition",
                  false,
                  false,
                  true
                )}
                placeholder="Offense City"
                variant="outlined"
              />
            </SecureFormItem>
          </StyledGridCol>

          <StyledGridCol lg={6}>
            <SecureFormItem
              fieldName="dispositionDate"
              formItemProps={{
                name: "dispositionDate",
                label: "Disposition date",
              }}
              overrideDisabled={!fullSearch}
            >
              <CommonDatePicker
                placeholder="MM/DD/YYYY"
                format="MM/DD/YYYY"
                showTime={false}
              />
            </SecureFormItem>
          </StyledGridCol>

          <StyledGridCol lg={6}>
            <SecureFormItem
              fieldName="dispositionAuthorizedBy"
              formItemProps={{
                name: "dispositionAuthorizedBy",
                label: "Authorized By",
                required: !fullSearch,
                rules: !fullSearch
                  ? [
                      {
                        required: true,
                        message: "Authorized by is required",
                      },
                    ]
                  : [],
              }}
            >
              <TextField />
            </SecureFormItem>
          </StyledGridCol>
        </StyledGridRow>
        <InmateDetailsTableContainer>
          <TableHeader>
            <h6>Court Dates</h6>
            <TableHeaderButton>
              <StyledFlex gap="10px">
                <Button
                  color="primary"
                  type="primary"
                  onClick={onAddClick}
                  disabled={!bookingsFieldsEnabled || isConfiguring}
                >
                  <PlusOutlined /> Add
                </Button>

                <Button
                  color="primary"
                  type="primary"
                  disabled={!selectedCourtsData || isConfiguring}
                  onClick={()=> setModalShown(() => true)}
                >
                  Details
                </Button>
                <Popconfirm
                  title="Delete the court date"
                  description="Are you sure to delete this court date?"
                  onConfirm={onDeleteClick}
                  onCancel={() => {}}
                  okText="Yes"
                  cancelText="No"
                >
                  <Button
                    color="primary"
                    type="primary"
                    disabled={!selectedCourtsData || !bookingsFieldsEnabled || isConfiguring}
                  >
                    Delete
                  </Button>
                </Popconfirm>

                {/* Config — drag-reorder/resize columns */}
                {!isConfiguring ? (
                  <Button
                    htmlType="button"
                    onClick={startConfiguring}
                  >
                    {configButtonLabel}
                  </Button>
                ) : (
                  <Popconfirm
                    title="Do you want to save the current list view configuration?"
                    onConfirm={confirmSave}
                    onCancel={discardChanges}
                    okText="Yes"
                    cancelText="No"
                  >
                    <Button htmlType="button">
                      {configButtonLabel}
                    </Button>
                  </Popconfirm>
                )}
              </StyledFlex>
            </TableHeaderButton>
          </TableHeader>
          <Table
            ref={dataTableRef}
            headers={courtHeaders}
            draggable={isConfiguring}
            data={
              tempCourtListsData?.map((el, index) => ({
                ...el,
                offenderDisposition: getRefListNameByID(
                  "OffenderDisposition",
                  el?.offenderDispositionID
                ),
                courtName: getRefListNameByID("Court", el?.courtNameID),
              })) || []
            }
            style={{ padding: "unset" }}
            onRowClick={(data) => setSelectedCourtsData(() => data)}
            onRowDoubleClick={(value) => {
              setSelectedCourtsData(() => value);
              setModalShown(() => true);
            }}
          />
        </InmateDetailsTableContainer>
      </FormContainer>
      </FieldSecurityProvider>
      {/* <IntakeSectionHeader>
        <span>Additional Court Information</span>
      </IntakeSectionHeader>
      <FormContainer>
        <Row gutter={16}>
          <Col span={12}>
            <FormItem label="Court Reason" layout="vertical">
              <Select
                options={getOptionsFromRefList("Court_Reason")}
                placeholder="Offense City"
                showSearch={false}
                variant="outlined"
              />
            </FormItem>
          </Col>
          <Col span={12}>
            <FormItem label="Charge" layout="vertical">
              <TextField />
            </FormItem>
          </Col>
        </Row>
      </FormContainer> */}
      {modalShown && (
        <CourtInformationModal
          show={modalShown}
          onHide={onModalHide}
          formData={selectedCourtsData}
          onCourtInformationSaveClick={onCourtInformationSaveClick}
          courtInfoRef={courtInfoRef}
          startingPoint={startingPoint}
        />
      )}
    </>
  );
};

export default CourtInformation;
